import { apiFetchAllowingUnauthorized } from "./api";

export interface CurrentUser {
  sub: string;
  username?: string;
  email?: string;
  displayName?: string;
  groups: string[];
  via: "session" | "bearer" | "disabled";
}

/** Resolves the signed-in user, or null when not signed in. Never redirects. */
export const fetchCurrentUser = async (): Promise<CurrentUser | null> => {
  try {
    const response = await apiFetchAllowingUnauthorized("/api/auth/me");
    if (!response.ok) return null;
    return (await response.json()) as CurrentUser;
  } catch {
    return null;
  }
};

/** Best available human-readable name, falling back through the claim set. */
export const displayNameFor = (user: CurrentUser): string =>
  user.displayName?.trim() || user.username?.trim() || user.email?.trim() || "Account";

/**
 * Up to two initials for the avatar. Prefers the parts of a full name, and
 * falls back to the first character of whatever identifier exists — `sub` is a
 * UUID, so it is deliberately the last resort.
 */
export const initialsFor = (user: CurrentUser): string => {
  const name = user.displayName?.trim() || user.username?.trim() || user.email?.trim();
  if (!name) return "?";

  const words = name.replace(/@.*$/, "").split(/[\s._-]+/).filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0].slice(0, 1).toUpperCase();
  return (words[0].slice(0, 1) + words[words.length - 1].slice(0, 1)).toUpperCase();
};
